import { Text } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';
import { AudioBus } from '../audio/AudioBus';
import type { Disposable } from '../core/Event';
import { DefaultRandom, type Random } from '../core/Random';
import { Easings, TweenGroup } from '../core/Tween';
import { WinParticles } from '../effects/WinParticles';
import { PlayerProfile } from '../model/profile/PlayerProfile';
import { DEFAULT_THEME, type Theme } from '../theme';
import { BalanceDisplay } from '../widgets/BalanceDisplay';
import { ChestWidget } from '../widgets/chest/ChestWidget';
import { PrimaryButton } from '../widgets/PrimaryButton';
import {
  rollChestPickRound,
  type ChestPickReward,
} from './fixtures/chestFixtures';
import { SceneBase } from './SceneBase';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

/** How many chests sit on the row. Locked at 3 by the producer's design —
 * fewer breaks "pick one of three", more crowds the centre. */
const CHEST_COUNT = 3;
/** Centre-to-centre spacing between adjacent chests. Sized so the row
 * (~500px wide for 3 chests) reads as a clear "pick one" line-up rather
 * than a single mound. */
const CHEST_SPACING = 250;
/** Per-chest body width — narrower than the original 220 so all three fit
 * comfortably on a phone-portrait viewport. */
const CHEST_WIDTH = 170;
/** Delay before the un-picked chests auto-reveal. Long enough that the
 * player's pick lands first; short enough that the FOMO punch is still in
 * the same beat. */
const FOMO_REVEAL_DELAY_MS = 1000;
/** Delay before CLAIM fades in. Picked from "feels reactive" rather than
 * "skips the moment" — the labels need time to settle. */
const CLAIM_FADE_DELAY_MS = 2500;
/** Alpha for the un-picked chests' reveal — visibly dimmer than the
 * picked one so the eye instantly groups picked vs missed. */
const UNPICKED_ALPHA = 0.55;

export interface ChestOpenSceneOptions {
  /** Called when the player taps CLAIM after the chest has opened. */
  onComplete?: () => void;
}

interface ChestSlot {
  widget: ChestWidget;
  particles: WinParticles;
  reward: ChestPickReward;
  index: number;
}

/**
 * Three-chest pick scene. The player taps one of three chests; each is
 * pre-loaded with a DUD / SMALL / BIG reward in shuffled order. The
 * picked chest opens with full celebration; the other two auto-reveal a
 * beat later at reduced alpha so the player can see what they could
 * have won (FOMO reveal — drives session-extending replays on free
 * world-map respawns).
 *
 * <p>Reward is rolled at scene build time so all three chests' contents
 * are fixed before the player chooses. Credit happens the moment the
 * pick fires — same reasoning as {@link ScratchDemoScene}: a mid-anim
 * canvas teardown (theme switch, route change) shouldn't cost the player
 * their reward.
 */
export class ChestOpenScene extends SceneBase {
  private readonly random: Random;
  private readonly profile: PlayerProfile;
  private readonly onComplete: (() => void) | null;

  private slots: ChestSlot[] = [];
  private rewards: readonly [
    ChestPickReward,
    ChestPickReward,
    ChestPickReward,
  ] | null = null;
  private balance: BalanceDisplay | null = null;
  private pickPrompt: Text | null = null;
  private rewardLabel: Text | null = null;
  private claimButton: PrimaryButton | null = null;
  private balanceSubscription: Disposable | null = null;
  private readonly tweens = new TweenGroup();
  private pickedIndex: number | null = null;
  private fomoTimeout: ReturnType<typeof setTimeout> | null = null;
  private claimTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    container: HTMLElement,
    theme: Theme = DEFAULT_THEME,
    profile: PlayerProfile,
    options: ChestOpenSceneOptions = {},
  ) {
    super(container, { theme });
    this.random = new DefaultRandom();
    this.profile = profile;
    this.onComplete = options.onComplete ?? null;
  }

  /** Load the theme's sprite + audio bundles before {@link buildContent}
   * runs. Critical for the {@code pickem:chest_*} aliases the
   * {@link ../widgets/chest/ChestWidget ChestWidget} resolves
   * synchronously in its constructor — without this preload the
   * widget falls through to its procedural Graphics fallback (still
   * functional, just no commissioned art). */
  protected async preload(): Promise<void> {
    await Promise.all([
      AssetLoader.shared.loadTheme(this.theme),
      AudioBus.shared.loadTheme(this.theme),
    ]);
  }

  protected buildContent(): void {
    const accent = this.theme.palette.accent;
    const warm = this.theme.palette.warm;

    // Balance display (top of viewport — same y as other scenes' HUDs).
    this.balance = new BalanceDisplay(this.profile.balance);
    this.app.stage.addChild(this.balance);
    this.balanceSubscription = this.profile.events.on(
      'balance-changed',
      (event) => {
        this.balance?.setAmount(event.after);
        this.balance?.bump();
      },
    );

    // Roll the round once, up-front. All three chests' contents are
    // fixed before the player picks; the only thing the tap decides is
    // which slot they reveal first.
    this.rewards = rollChestPickRound(this.random);

    for (let i = 0; i < CHEST_COUNT; i++) {
      const reward = this.rewards[i] as ChestPickReward;
      // Each chest gets its own particle layer so the picked chest's
      // celebration burst doesn't overlap onto the dim FOMO chests.
      const particles = new WinParticles(this.app.ticker);
      this.app.stage.addChild(particles);

      const widget = new ChestWidget({
        width: CHEST_WIDTH,
        bodyColor: 0x8b5a2b,
        trimColor: warm,
        glowColor: accent,
        // Per-chest hints would render three identical "TAP TO OPEN"
        // labels stacked under the row — visual noise. The scene shows
        // a single "PICK A CHEST" prompt above the row instead.
        hint: null,
        // Asset brief pack 3 §30 sprites + Veo opening video. The
        // widget falls back to the procedural Graphics body+lid when
        // any of these aren't loaded — keeps the scene rendering on
        // themes that haven't shipped commissioned chest art yet.
        closedTextureAlias: 'pickem:chest_closed',
        openTextureAlias: 'pickem:chest_open',
        openVideoAlias: 'pickem:chest_open_video',
        onTap: () => void this.handlePick(i),
      });
      this.app.stage.addChild(widget);

      this.slots.push({ widget, particles, reward, index: i });
    }

    // Row-level prompt — replaces the per-chest "TAP TO OPEN" hint so
    // we don't render three identical labels stacked. Sits above the
    // chest row and fades out the moment the player picks one.
    this.pickPrompt = new Text({
      text: 'PICK A CHEST',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 18,
        fontWeight: '900',
        fill: 0xffffff,
        letterSpacing: 4,
      },
    });
    this.pickPrompt.anchor.set(0.5);
    this.app.stage.addChild(this.pickPrompt);

    // Single shared reward label — sits below the picked chest. The
    // string adapts to the outcome (dud → "no win", small → "+N",
    // big → "+N coins · +M gems").
    this.rewardLabel = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 28,
        fontWeight: '900',
        fill: 0xfff59d,
        align: 'center',
        stroke: { color: 0xff6bd6, width: 4 },
      },
    });
    this.rewardLabel.anchor.set(0.5);
    this.rewardLabel.alpha = 0;
    this.app.stage.addChild(this.rewardLabel);

    this.claimButton = new PrimaryButton({
      label: 'CLAIM',
      width: 200,
      fillActive: accent,
      fillHover: darken(accent, 0.7),
      textColor: 0xffffff,
      onTap: () => this.onComplete?.(),
    });
    this.claimButton.visible = false;
    this.app.stage.addChild(this.claimButton);

    this.layout();
  }

  protected onResize(): void {
    this.layout();
  }

  protected disposeResources(wasInitialized: boolean): void {
    this.balanceSubscription?.dispose();
    this.balanceSubscription = null;
    this.tweens.cancelAll();
    if (this.fomoTimeout) {
      clearTimeout(this.fomoTimeout);
      this.fomoTimeout = null;
    }
    if (this.claimTimeout) {
      clearTimeout(this.claimTimeout);
      this.claimTimeout = null;
    }
    if (!wasInitialized) return;
    for (const slot of this.slots) {
      slot.widget.destroyChest();
      slot.widget.destroy({ children: true });
      slot.particles.destroyEffect();
      slot.particles.destroy({ children: true });
    }
    this.slots = [];
    this.balance?.destroyDisplay();
    this.balance?.destroy({ children: true });
    this.balance = null;
    this.pickPrompt?.destroy();
    this.pickPrompt = null;
    this.rewardLabel?.destroy();
    this.rewardLabel = null;
    this.claimButton?.destroy({ children: true });
    this.claimButton = null;
  }

  private layout(): void {
    if (!this.balance || this.slots.length === 0) return;
    const { width, height } = this.app.renderer;
    const centerX = Math.round(width / 2);
    const centerY = Math.round(height / 2);

    this.balance.x = centerX;
    this.balance.y = 16;

    // Centre the row of chests on centerX. With 3 chests and CHEST_SPACING
    // between centres, slot i sits at centerX + (i - (n-1)/2) * spacing.
    const rowOffset = (CHEST_COUNT - 1) / 2;
    const chestY = centerY - 40;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i] as ChestSlot;
      const x = centerX + Math.round((i - rowOffset) * CHEST_SPACING);
      slot.widget.x = x;
      slot.widget.y = chestY;
      slot.particles.x = x;
      slot.particles.y = chestY - 20;
    }

    if (this.pickPrompt) {
      // Sits above the chest row, far enough up that the open-lid
      // rotation arc clears the prompt — and the prompt is fading out
      // anyway when a pick fires.
      this.pickPrompt.x = centerX;
      this.pickPrompt.y = chestY - CHEST_WIDTH * (2 / 3) / 2 - 56;
    }

    if (this.rewardLabel) {
      // Anchor the reward label below whichever chest was picked, or
      // centred above the row before any pick has happened.
      const labelX =
        this.pickedIndex !== null
          ? (this.slots[this.pickedIndex] as ChestSlot).widget.x
          : centerX;
      this.rewardLabel.x = labelX;
      this.rewardLabel.y = centerY + 100;
    }
    if (this.claimButton) {
      this.claimButton.x = centerX;
      this.claimButton.y = Math.min(height - 70, centerY + 200);
    }
  }

  private async handlePick(index: number): Promise<void> {
    if (this.pickedIndex !== null) return;
    const picked = this.slots[index];
    if (!picked) return;
    this.pickedIndex = index;

    // 1) Credit the picked reward immediately — see class doc.
    const reward = picked.reward;
    if (reward.coins > 0) this.profile.credit(reward.coins);
    if (reward.gems > 0) this.profile.awardGems(reward.gems);

    // 2) Lock the un-picked chests' tap handlers (the widget itself
    //    gates re-taps on isOpen, but we don't want a fast double-tap
    //    on a dud to register as picking the big chest).
    for (const slot of this.slots) {
      if (slot.index === index) continue;
      slot.widget.eventMode = 'auto';
      slot.widget.cursor = 'default';
    }

    // 3) Re-layout so the reward label snaps under the picked chest.
    this.layout();

    // 4a) Fade the row prompt — its job is done the moment a chest is
    //     picked, and we don't want it lingering over the celebration.
    if (this.pickPrompt) {
      const promptStart = this.pickPrompt.alpha;
      this.tweens.start({
        durationMs: 220,
        easing: Easings.easeOutCubic,
        onUpdate: (p) => {
          if (this.pickPrompt) this.pickPrompt.alpha = promptStart * (1 - p);
        },
      });
    }

    // 4) Run the picked chest's open animation + full-celebration burst.
    this.fireBurst(picked, /* dimmed */ false);
    await picked.widget.open();

    // 5) Reveal the picked reward.
    this.revealRewardLabel(reward);

    // 6) After a beat, auto-open the un-picked chests at reduced alpha
    //    so the player sees what they missed.
    this.fomoTimeout = setTimeout(() => {
      this.fomoTimeout = null;
      this.revealUnpicked();
    }, FOMO_REVEAL_DELAY_MS);

    // 7) After a longer beat, fade in the CLAIM button. Independent of
    //    the FOMO reveal so the timing of CLAIM is keyed to the player's
    //    pick (not to whichever animation finishes last).
    this.claimTimeout = setTimeout(() => {
      this.claimTimeout = null;
      this.fadeInClaim();
    }, CLAIM_FADE_DELAY_MS);
  }

  private revealUnpicked(): void {
    for (const slot of this.slots) {
      if (slot.index === this.pickedIndex) continue;
      // Dim the chest itself so the body+lid render as "missed". The
      // open() animation runs on the dimmed alpha.
      this.tweens.start({
        durationMs: 280,
        easing: Easings.easeOutCubic,
        onUpdate: (p) => {
          slot.widget.alpha = 1 - (1 - UNPICKED_ALPHA) * p;
        },
      });
      this.fireBurst(slot, /* dimmed */ true);
      void slot.widget.open();
    }
  }

  private fireBurst(slot: ChestSlot, dimmed: boolean): void {
    const accent = this.theme.palette.accent;
    const warm = this.theme.palette.warm;
    const reward = slot.reward;
    // Particle counts scale with outcome — a dud reveals a small puff so
    // the chest doesn't look broken; small gets a modest burst; big
    // gets the full party. Dimmed (un-picked) bursts are 35% of the
    // full count so the visual hierarchy stays "your pick > the rest".
    let count: number;
    if (reward.outcome === 'dud') {
      count = 18;
    } else if (reward.outcome === 'small') {
      count = 70;
    } else {
      count = 140;
    }
    if (dimmed) count = Math.round(count * 0.35);

    // Dud puffs use neutral grey/cream tints (no win-colour reds /
    // blues that would falsely sell a reward). Small/big share the
    // win palette but with gem-blue weighted only on big.
    const tints =
      reward.outcome === 'dud'
        ? [0xc8c8c8, 0xe6e0d4, 0xa39a8a]
        : reward.outcome === 'small'
          ? [warm, accent, 0xfff59d, 0xffe066]
          : [warm, accent, 0xfff59d, 0x9be7ff, 0xffe066];

    slot.particles.burst(count, {
      tints,
      lifeMs: reward.outcome === 'dud' ? 700 : 1300,
      minSpeed: reward.outcome === 'dud' ? 80 : 200,
      maxSpeed: reward.outcome === 'dud' ? 200 : 520,
      gravity: 600,
      radius: reward.outcome === 'dud' ? 3 : 5,
    });
  }

  private revealRewardLabel(reward: ChestPickReward): void {
    if (!this.rewardLabel) return;
    this.rewardLabel.text = formatRewardLabel(reward);
    // Dud uses a softer fill so the "no win" line doesn't sell as
    // celebration. Coin/gem rewards keep the warm-yellow + pink stroke
    // that the rest of the engine uses for win moments.
    this.rewardLabel.style.fill =
      reward.outcome === 'dud' ? 0xd8d8d8 : 0xfff59d;
    this.rewardLabel.style.stroke =
      reward.outcome === 'dud'
        ? { color: 0x4a4a4a, width: 3 }
        : { color: 0xff6bd6, width: 4 };
    this.tweens.start({
      durationMs: 320,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        if (!this.rewardLabel) return;
        this.rewardLabel.alpha = p;
        this.rewardLabel.scale.set(0.7 + p * 0.3);
      },
    });
  }

  private fadeInClaim(): void {
    if (!this.claimButton) return;
    this.claimButton.visible = true;
    this.claimButton.alpha = 0;
    this.tweens.start({
      durationMs: 360,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        if (!this.claimButton) return;
        this.claimButton.alpha = p;
      },
    });
  }
}

function formatRewardLabel(reward: ChestPickReward): string {
  if (reward.outcome === 'dud') return '— no win —';
  const parts: string[] = [];
  if (reward.coins > 0) parts.push(`+${reward.coins.toLocaleString()} coins`);
  if (reward.gems > 0) parts.push(`+${reward.gems} gems`);
  // Defensive — a small/big roll with both ranges hitting 0 shouldn't
  // happen, but if it ever does, fall back to the dud label rather than
  // showing an empty string.
  if (parts.length === 0) return '— no win —';
  return parts.join(' · ');
}

function darken(color: number, factor: number): number {
  const r = Math.round(((color >> 16) & 0xff) * factor);
  const g = Math.round(((color >> 8) & 0xff) * factor);
  const b = Math.round((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}
